import mapboxgl from 'mapbox-gl';
import { createContext, MutableRefObject, useContext } from 'react';

// Module-level "came from detail" handoff. The detail page sets this true in its
// unmount cleanup; the list page reads + consumes it on mount to decide whether to
// animate back to the default overview instead of snapping there instantly.
let cameFromDetailFlag = false;

export const setCameFromDetail = (v: boolean): void => {
    cameFromDetailFlag = v;
};

export const consumeCameFromDetail = (): boolean => {
    const v = cameFromDetailFlag;
    cameFromDetailFlag = false;

    return v;
};

// Tracks which Track route currently owns the shared map's data sources.
// Guards against fast-nav races: e.g. list → detail → list → detail where a
// stale setOrderMarker retry fires after the detail page has mounted and
// repopulates the orders source on top of the detail's route line.
type ActiveTrackView = 'list' | 'detail' | null;
let activeTrackView: ActiveTrackView = null;

export const getActiveTrackView = (): ActiveTrackView => activeTrackView;

export const setActiveTrackView = (v: ActiveTrackView): void => {
    activeTrackView = v;
};

// Monotonic counter for the detail page's Directions API calls. Lives at module
// level because the detail page unmounts + remounts on list→detail→list→detail
// navigation; a per-instance useRef gets a fresh closure each mount and cannot
// invalidate fetches from a previous mount. Bumped on every mount, unmount, and
// every new updateRouteAndMarkers call.
let trackRouteRequestSeq = 0;

export const bumpTrackRouteRequestSeq = (): number => {
    trackRouteRequestSeq += 1;

    return trackRouteRequestSeq;
};

export const getTrackRouteRequestSeq = (): number => trackRouteRequestSeq;

export interface ITrackMapContext {
    // The single persistent Mapbox instance, created exactly once by TrackMapLayout
    // and shared between /track (list) and /track/:id (detail).
    mapRef: MutableRefObject<mapboxgl.Map | null>;
    // true once the persistent Mapbox instance has fired its `load` event. Tracked
    // as React state so child routes re-render and re-run their map-setup effects
    // when the map becomes ready — needed for the detail page's route-line draw.
    isMapReady: boolean;
    // Move the persistent map's DOM node into the child's existing slot.
    // Called from each child's map-init effect instead of `new mapboxgl.Map(...)`.
    attachMapTo: (target: HTMLElement | null) => void;
    // Move the map's DOM node back to the layout's hidden stash so the Mapbox
    // instance stays alive after the child unmounts.
    returnToStash: () => void;

    // ── Map appearance ────────────────────────────────────────────────────────
    // mapStyle and showTimezones are persisted per user in localStorage (see
    // track.preferences.ts) — localStorage only, no backend copy, unlike WAL.

    // Mapbox style URL currently applied. Owned by TrackMapLayout so both the list
    // and detail pages read the same value.
    mapStyle: string;
    // Swap the base style. Clears stale DOM markers, calls map.setStyle(), and bumps
    // styleEpoch once the new style has loaded. Never call map.setStyle() directly —
    // going through here is what tells the pages to re-add their layers.
    setMapStyle: (styleUrl: string) => void;
    // Increments after every completed style load. Each page re-adds ITS OWN sources
    // and layers from a `useEffect([styleEpoch])`, because setStyle() destroys them.
    // Centralised here rather than duplicating a `once('style.load')` handler in both
    // pages (which is what WAL does) — CP shares one map between them, so a single
    // owner avoids double-registration and drift.
    styleEpoch: number;
    // Whether the UTC timezone-boundary overlay is visible.
    showTimezones: boolean;
    // Toggle the timezone overlay. Fetches + adds the layers on first enable, then
    // just flips layer visibility on subsequent toggles.
    toggleTimezones: () => void;
}

export const TrackMapContext = createContext<ITrackMapContext | null>(null);

export const useTrackMap = (): ITrackMapContext => {
    const ctx = useContext(TrackMapContext);

    if (!ctx) {
        throw new Error('useTrackMap must be used inside TrackMapLayout');
    }

    return ctx;
};
